import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faThumbsUp, faThumbsDown, faImage, faLink } from '@fortawesome/free-solid-svg-icons';
import BottomSheet from '../BottomSheet';

export default function DecisionCardDetailModal({ card, onClose, onUpdate }) {
  const [gallery, setGallery] = useState(card.gallery || []);
  const [newImageUrl, setNewImageUrl] = useState('');
  const [selectedOption, setSelectedOption] = useState(card.selectedOption || '');
  const [textValue, setTextValue] = useState(card.textValue || '');

  const handleAddImage = async (type, value) => {
    if (type === 'url') {
      if (!value.trim()) return;
      
      // For now, just add the URL directly
      // In production, you'd fetch and extract thumbnails
      const newImage = {
        type: 'image',
        url: value,
        thumbnail: value,
        rating: 0,
        addedBy: 'current-user' // Replace with actual user ID
      };
      
      const updated = [...gallery, newImage];
      setGallery(updated);
      onUpdate({ gallery: updated });
      setNewImageUrl('');
    } else if (type === 'upload') {
      // Handle file upload - would need Firebase Storage integration
      console.log('File upload not yet implemented');
    }
  };

  const updateImageRating = (index, rating) => {
    const updated = gallery.map((img, i) => 
      i === index ? { ...img, rating } : img
    );
    setGallery(updated);
    onUpdate({ gallery: updated });
  };

  const handleOptionSelect = (option) => {
    const value = option.value || option;
    setSelectedOption(value);
    onUpdate({ selectedOption: value });
    
    // Trigger conditional cards if needed
    if (option.triggers) {
      // This would be handled by the parent component
      console.log('Trigger cards:', option.triggers);
    }
  };

  const handleTextUpdate = (value) => {
    setTextValue(value);
    onUpdate({ textValue: value });
  };

  const handleStatusChange = (status) => {
    onUpdate({ status, kanbanColumn: status });
  };

  return (
    <BottomSheet isOpen={true} onClose={onClose} title={card.title} fullScreen={true}>
      <div className="p-6 space-y-8">
        {/* Card description */}
        {card.description && (
          <p className="text-text-secondary">{card.description}</p>
        )}

        {/* Help text */}
        {card.helpText && (
          <div className="bg-primary/5 border border-primary/20 rounded-xl p-4">
            <p className="text-sm text-primary-dark">{card.helpText}</p>
          </div>
        )}

        {/* Render based on card type */}
        {card.type === 'gallery' && (
          <div className="space-y-6">
            <h3 className="font-bold text-text-primary">Add Images & Ideas</h3>
            
            {/* Add image controls */}
            <div className="flex gap-3">
              <input
                type="text"
                placeholder="Paste a URL..."
                value={newImageUrl}
                onChange={(e) => setNewImageUrl(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddImage('url', newImageUrl)}
                className="flex-1 px-4 py-3 border-2 border-border rounded-xl"
              />
              <button
                onClick={() => handleAddImage('url', newImageUrl)}
                className="px-6 py-3 bg-primary text-white font-bold rounded-xl"
              >
                <FontAwesomeIcon icon={faLink} />
              </button>
              <label className="px-6 py-3 bg-white border-2 border-border font-bold rounded-xl cursor-pointer hover:border-primary transition-all flex items-center gap-2">
                <FontAwesomeIcon icon={faImage} />
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleAddImage('upload', e.target.files[0])}
                />
              </label>
            </div>

            {/* Gallery grid with ratings */}
            {gallery.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {gallery.map((item, index) => (
                  <div key={index} className="space-y-2">
                    <div className="aspect-square rounded-xl overflow-hidden bg-gray-100">
                      <img 
                        src={item.thumbnail || item.url} 
                        alt="" 
                        className="w-full h-full object-cover"
                      />
                    </div>
                    
                    {/* Likert scale rating */}
                    <div className="flex justify-center gap-1">
                      {[-2, -1, 0, 1, 2].map(rating => (
                        <button
                          key={rating}
                          onClick={() => updateImageRating(index, rating)}
                          className={`
                            p-2 rounded-lg transition-all
                            ${item.rating === rating 
                              ? rating < 0 ? 'bg-red-100 text-red-600'
                                : rating > 0 ? 'bg-green-100 text-green-600'
                                : 'bg-gray-100 text-gray-600'
                              : 'bg-gray-50 text-gray-400 hover:bg-gray-100'
                            }
                          `}
                        >
                          <FontAwesomeIcon 
                            icon={rating < 0 ? faThumbsDown : faThumbsUp}
                            className={rating === -2 || rating === 2 ? 'text-lg' : 'text-sm'}
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {card.type === 'options' && (
          <div className="space-y-4">
            <h3 className="font-bold text-text-primary">Choose an Option</h3>
            
            {card.options?.map(option => {
              const optionValue = option.value || option;
              const optionLabel = option.label || option;
              const isSelected = selectedOption === optionValue;
              
              return (
                <button
                  key={optionValue}
                  onClick={() => handleOptionSelect(option)}
                  className={`
                    w-full p-4 rounded-xl border-2 text-left transition-all
                    ${isSelected
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary'
                    }
                  `}
                >
                  <span className="font-semibold">{optionLabel}</span>
                </button>
              );
            })}
          </div>
        )}

        {card.type === 'text' && (
          <div className="space-y-4">
            <h3 className="font-bold text-text-primary">Your Answer</h3>
            <textarea
              value={textValue}
              onChange={(e) => setTextValue(e.target.value)}
              onBlur={() => handleTextUpdate(textValue)}
              rows={6}
              className="w-full px-4 py-3 border-2 border-border rounded-xl resize-none"
              placeholder="Type your thoughts here..."
            />
          </div>
        )}

        {card.type === 'date' && (
          <div className="space-y-4">
            <h3 className="font-bold text-text-primary">Choose Date</h3>
            <input
              type="date"
              value={card.selectedDate || ''}
              onChange={(e) => onUpdate({ selectedDate: e.target.value })}
              className="w-full px-4 py-3 border-2 border-border rounded-xl"
            />
          </div>
        )}

        {card.type === 'guestlist' && (
          <div className="space-y-4">
            <h3 className="font-bold text-text-primary">Guest List</h3>
            <p className="text-text-secondary">Guest list management coming soon...</p>
          </div>
        )}

        {/* Move to kanban column */}
        <div className="space-y-3">
          <h3 className="font-bold text-text-primary">Where should this go?</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {['later', 'to-discuss', 'in-discussion', 'decided'].map(column => (
              <button
                key={column}
                onClick={() => handleStatusChange(column)}
                className={`
                  px-4 py-3 rounded-xl font-semibold transition-all
                  ${card.status === column
                    ? 'bg-primary text-white'
                    : 'bg-white border-2 border-border hover:border-primary'
                  }
                `}
              >
                {column === 'to-discuss' ? 'To Discuss' :
                 column === 'in-discussion' ? 'In Discussion' :
                 column === 'decided' ? 'Decided' :
                 'Later'}
              </button>
            ))}
          </div>
        </div>

        {/* Save button */}
        <div className="flex gap-3 pt-4">
          <button
            onClick={onClose}
            className="flex-1 bg-primary text-white font-bold py-4 rounded-xl"
          >
            Done
          </button>
        </div>
      </div>
    </BottomSheet>
  );
}
